import React, { useState } from 'react';
import { Layout } from '../../components/layout/Layout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  CheckSquare,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  CheckCircle2,
  ClipboardList
} from 'lucide-react';
import { router } from '@inertiajs/react';

const Index = ({ pending, approved, rejected, user, routes }) => {

  const handleApprove = (requestId) => {
    if (confirm('Are you sure you want to approve this travel request?')) {
      router.patch(`/approvals/${requestId}/approve`);
    }
  };

  const handleReject = (requestId) => {
    if (confirm('Are you sure you want to reject this travel request?')) {
      router.patch(`/approvals/${requestId}/reject`);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDateShort = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const formatSubmissionDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };



  const renderRequestsTable = (requests) => {
    if (requests.length === 0) {
      return (
        <div className="rounded-md border p-6 text-center">
          <ClipboardList className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium mb-2">No Requests</h3>
          <p className="text-sm text-gray-500">
            There are no travel requests to display in this category.
          </p>
        </div>
      );
    }

    return (
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Employee</TableHead>
              <TableHead>Destination</TableHead>
              <TableHead>Dates</TableHead>
              <TableHead>Total Cost</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Submitted</TableHead>
              <TableHead className="w-[120px]"><span className="ml-auto w-fit block text-right">Actions</span></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {requests.map((request) => (
              <TableRow key={request.id}>
                <TableCell className="font-medium">
                  {request.user?.name || "Unknown"}
                </TableCell>
                <TableCell>{request.city}</TableCell>
                <TableCell>
                  {formatDateShort(request.date_from)} - {formatDateShort(request.date_to)}
                </TableCell>
                <TableCell>
                  {formatCurrency((parseFloat(request.flight_cost) || 0) + (parseFloat(request.hotel_cost) || 0))}
                </TableCell>
                <TableCell>
                  <Badge className={`${
                    request.status === 'Approved' ? 'bg-green-100 text-green-800' :
                    request.status === 'Rejected' ? 'bg-red-100 text-red-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {request.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {formatSubmissionDate(request.created_at)}
                </TableCell>
                <TableCell className="text-right">
                  <div className="ml-auto flex gap-1 w-fit">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 px-2 bg-white"
                      onClick={() => handleApprove(request.id)}
                    >
                      <CheckCircle2 className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="default"
                      size="sm"
                      className="h-8 px-2"
                      onClick={() => handleReject(request.id)}
                    >
                      <XCircle className="h-3 w-3" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  return (
    <Layout requireAuth={true}>
      <div className="p-6">
        <div className="max-w-7xl mx-auto space-y-6">

          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-2xl">Approval Dashboard</CardTitle>
              <CardDescription>Manage and review travel requests from your team</CardDescription>
            </CardHeader>
          </Card>

          <Tabs defaultValue="pending">
            <TabsList className="mb-6">
              <TabsTrigger value="pending" className="flex gap-2">
                <AlertCircle className="h-4 w-4" />
                Pending ({pending.length})
              </TabsTrigger>
              <TabsTrigger value="approved" className="flex gap-2">
                <CheckCircle2 className="h-4 w-4" />
                Approved ({approved.length})
              </TabsTrigger>
              <TabsTrigger value="rejected" className="flex gap-2">
                <XCircle className="h-4 w-4" />
                Rejected ({rejected.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="pending">
              {renderRequestsTable(pending)}
            </TabsContent>

            <TabsContent value="approved">
              {renderRequestsTable(approved)}
            </TabsContent>

            <TabsContent value="rejected">
              {renderRequestsTable(rejected)}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </Layout>
  );
};

export default Index;
